import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Jetons d'authentification en localStorage — nécessaires pour l'app Capacitor:
// WKWebView (iOS) bloque les cookies cross-origin (capacitor://localhost →
// https://neldaari.com). Le serveur vérifie le cookie httpOnly D'ABORD (web
// inchangé), puis le header "Authorization: Bearer" (app mobile).
// ---------------------------------------------------------------------------
const TOKENS_KEY = "neldaari_auth_tokens";

export function getStoredTokens() {
  try {
    return JSON.parse(localStorage.getItem(TOKENS_KEY)) || null;
  } catch {
    return null;
  }
}

export function setStoredTokens(tokens) {
  try {
    if (tokens) localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    else localStorage.removeItem(TOKENS_KEY);
  } catch (e) { void e; }
}

export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Inject Accept-Language + Authorization Bearer (si tokens stockés)
apiClient.interceptors.request.use((config) => {
  try {
    const lang = (typeof localStorage !== "undefined" && localStorage.getItem("neldaari_lang")) || "fr";
    config.headers = config.headers || {};
    config.headers["Accept-Language"] = lang === "en" ? "en" : "fr";
    const tokens = getStoredTokens();
    const bearer = tokens?.access_token || tokens?.session_token;
    if (bearer) config.headers["Authorization"] = `Bearer ${bearer}`;
  } catch (e) { void e; }
  return config;
});

// 1) Capture automatique des tokens renvoyés par login/register/refresh/OTP…
// 2) Renouvellement silencieux : sur 401, tente POST /auth/refresh avec le
//    refresh token stocké, puis rejoue la requête d'origine (une seule fois).
let refreshPromise = null;
const AUTH_NO_RETRY = /\/auth\/(login|register|refresh|logout|phone|mfa|google)/;

apiClient.interceptors.response.use(
  (response) => {
    const t = response?.data?.auth_tokens;
    if (t) {
      setStoredTokens({ ...(getStoredTokens() || {}), ...t });
      delete response.data.auth_tokens;
    }
    return response;
  },
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    const refreshToken = getStoredTokens()?.refresh_token;
    if (status === 401 && refreshToken && original && !original._retry && !AUTH_NO_RETRY.test(url)) {
      original._retry = true;
      try {
        refreshPromise =
          refreshPromise ||
          axios.post(`${API}/auth/refresh`, { refresh_token: refreshToken }, { withCredentials: true });
        const { data } = await refreshPromise;
        refreshPromise = null;
        if (data?.auth_tokens) setStoredTokens({ ...(getStoredTokens() || {}), ...data.auth_tokens });
        return apiClient(original);
      } catch (e) {
        refreshPromise = null;
        setStoredTokens(null);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Une erreur est survenue";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" • ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
}
